// returnJob.js
// -----------------------------------------------------------------------------
// Every rule about a return (RT, SAP ORDN) on a booking, in one place.
//
// A booking is one of three jobs:
//   'delivery'  -- an SO, or a นัดหมาย (no jobType stored: every booking made
//                  before this file existed reads as this, with no backfill)
//   'exchange'  -- an SO that also collects the items of an RT on the same visit
//                  (the SO's U_KSK_ReturnNo, else an RT of the SO's original
//                  sale -- see saleSoOf)
//   'return'    -- an RT with no SO to deliver: soNo null, rtNo set. Not a
//                  นัดหมาย, although both have soNo null -- isAppointment is the
//                  test, never `!soNo`.
//
// Pure CommonJS: no Firestore, no Vue. Covered by npm run test:returns.
// Design: docs/superpowers/specs/2026-10-08-return-exchange-design.md
// -----------------------------------------------------------------------------
const R = require('./bookingRules')
const I = require('./installTime')

// A pickup, or the removal half of an exchange: one flat figure, not a tier by
// item count. Decided with delivery on 2026-10-08.
const RETURN_MINUTES = I.RETURN_MINUTES

const JOB_TYPES = ['delivery', 'exchange', 'return']

function text (v) { return v == null ? '' : String(v).trim() }
// SAP sends 0 / null for "no document"; both mean none.
function docNo (v) { const s = text(v); return s && s !== '0' ? s : '' }

function jobTypeOf (b) {
  const t = b && b.jobType
  return JOB_TYPES.indexOf(t) !== -1 ? t : 'delivery'
}
function isReturnOnly (b) { return jobTypeOf(b) === 'return' }
function hasReturn (b) { return jobTypeOf(b) !== 'delivery' }
function isAppointment (b) { return !docNo(b && b.soNo) && !docNo(b && b.rtNo) }

// What a list shows where it used to show `soNo || 'นัดหมาย'`.
function refText (b) {
  const so = docNo(b && b.soNo)
  const rt = docNo(b && b.rtNo)
  if (so && rt) return `${so} · RT ${rt}`
  if (so) return so
  if (rt) return `RT ${rt}`
  return 'นัดหมาย'
}

const RT_PROBLEM_TH = {
  CANCELLED: 'เลขที่ RT นี้ถูกยกเลิกแล้ว',
  CLOSED: 'เลขที่ RT นี้ถูกปิดแล้ว',
  DISPATCHED: 'เลขที่ RT นี้อยู่ในใบจัดรถแล้ว'
}

// Is the RT on a trip sheet of its own? ORDN.U_KSK_TransNo is COPIED from the
// original sale when the RT is created and only overwritten when dispatch puts
// the RT on a sheet (checked live 2026-10-09: RT 226100021 held 45537, its
// sale's sheet; RT 226100012 held 46923 against its sale's 42683). So a transNo
// counts only when it differs from the sale's. GetRT sends the sale's as
// saleTransNo. Empty transNo -> no. No sale link or no saleTransNo to compare
// against -> a set transNo counts (the safe answer). Views never read
// rt.transNo themselves.
function rtDispatched (rt) {
  const r = rt || {}
  const t = docNo(r.transNo)
  if (!t) return false
  if (!docNo(r.soNo)) return true
  const sale = docNo(r.saleTransNo)
  return !sale || sale !== t
}

// The same three checks fetchSo makes of an SO, in the same order.
function rtProblem (rt) {
  const r = rt || {}
  if (r.canceled === 'Y') return 'CANCELLED'
  if (r.status === 'C') return 'CLOSED'
  if (rtDispatched(r)) return 'DISPATCHED'
  return null
}

// The Thai line for rtProblem, or ''. A dispatched RT names its sheet, as the
// SO tab's "ถูกใช้งานโดยใบจัดรถเลขที่ …" does, so staff know which run holds it.
function rtProblemText (rt) {
  const problem = rtProblem(rt)
  if (!problem) return ''
  if (problem === 'DISPATCHED') return `เลขที่ RT นี้อยู่ในใบจัดรถเลขที่ ${docNo(rt.transNo)} แล้ว`
  return RT_PROBLEM_TH[problem]
}

// Which RT an SO has: its own U_KSK_ReturnNo first, else the newest row of
// GetRtBySo. `others` are the rest, for the form to mention -- an SO with two
// open returns is rare enough that a person should see it.
function pickRt (soRtNo, bySoRows) {
  const own = docNo(soRtNo)
  const listed = (Array.isArray(bySoRows) ? bySoRows : []).map(r => docNo(r && r.rtNo)).filter(Boolean)
  const chosen = own || listed[0] || ''
  if (!chosen) return null
  return { rtNo: chosen, others: listed.filter(n => n !== chosen) }
}

// The RT an SO-path lookup must use before any guess, or null when only the
// original sale's newest RT (GetRtBySo) is left to go on. A guess never
// replaces a known RT:
//   steered      -- the RT typed on the รับคืน tab that steered to this SO;
//                   a person named it, so it wins. conflict names the SO's own
//                   U_KSK_ReturnNo when that is a different RT, for a note.
//   soRtNo       -- the SO's U_KSK_ReturnNo (SAP wins over the booking).
//   bookingRtNo  -- the RT the booking already carries (คิวรถเล็ก's confirm).
function knownRt ({ steered, soRtNo, bookingRtNo } = {}) {
  const typed = docNo(steered)
  const own = docNo(soRtNo)
  if (typed) return { rtNo: typed, conflict: own && own !== typed ? own : '' }
  if (own) return { rtNo: own, conflict: '' }
  const held = docNo(bookingRtNo)
  if (held) return { rtNo: held, conflict: '' }
  return null
}

// คิวรถเล็ก's confirm: is the RT it is about to stamp held by a live bookingSlot
// other than the one this job came from? holder is findActiveBookingForRt's
// answer. The job's own slot (sourceSlotId), or a job that already carries this
// RT itself, is not "elsewhere".
function rtHeldElsewhere (holder, { sourceSlotId, ownRtNo, rtNo } = {}) {
  if (!holder) return false
  if (sourceSlotId && holder.id === sourceSlotId) return false
  const own = docNo(ownRtNo)
  if (own && own === docNo(rtNo)) return false
  return true
}

function returnLines (rows) {
  if (!Array.isArray(rows)) return []
  return rows.map(row => ({
    code: text(row && row.prodCode),
    name: I.productName(row),
    qty: I.productQty(row) || 1
  })).filter(p => p.name)
}

// The booking fields an RT contributes. One function, so the SO path, the RT
// path and attachRt cannot list them differently -- the lesson of geo and
// landmark, dropped twice by hops that wrote their own field lists.
function rtFields (rt, prodRows, exchange) {
  const rows = Array.isArray(prodRows) ? prodRows : []
  return {
    rtNo: docNo(rt && rt.rtNo),
    rtDocEntry: (rt && rt.docEntry) || null,
    jobType: exchange ? 'exchange' : 'return',
    prodListRt: rows,
    rtItemCount: returnLines(rows).reduce((n, p) => n + p.qty, 0)
  }
}

// An exchange in SAP is two SOs, and the RT links to the OLD one (checked
// against SAP on 2026-10-09):
//
//   SO 126090384 (original sale)
//     ^ ORDR.U_KSK_SaleOrderNo       ^ ORDN.U_KSK_SaleOrderNo
//   SO 226100015 --ORDR.U_KSK_ReturnNo--> RT 226100011
//   (new SO for the exchange)             (the return)
//
// So GetRT's soNo is the original sale -- display only -- and the new SO is
// found from its own side (GetSoByRt).

// The original sale an SO belongs to: GetProdList rows carry the header's
// U_KSK_SaleOrderNo as saleSo. What GetRtBySo is asked with when the SO's own
// U_KSK_ReturnNo is blank -- never the SO's own number, which no RT carries.
function saleSoOf (prodRows) {
  if (!Array.isArray(prodRows)) return ''
  for (const row of prodRows) {
    const n = docNo(row && row.saleSo)
    if (n) return n
  }
  return ''
}

// The exchange SO an RT is on, from GetSoByRt's rows (newest first). match is
// 'returnNo' (the SO's U_KSK_ReturnNo names the RT) or 'saleOrder' (an open SO
// of the same original sale -- a guess a person confirms). others are the rest.
function pickExchangeSo (rows) {
  const list = (Array.isArray(rows) ? rows : []).filter(r => docNo(r && r.soNo))
  if (!list.length) return null
  return {
    soNo: docNo(list[0].soNo),
    match: list[0].match,
    others: list.slice(1).map(r => docNo(r.soNo))
  }
}

// The รับคืน tab: what to do with an RT.
//   soNo       -- the exchange SO GetSoByRt found (pickExchangeSo), or '' when
//                 none or a person declined a 'saleOrder' match. Never the
//                 RT's own soNo, which is the original sale.
//   so         -- GetSo row for soNo, or null when SAP has none
//   soBooking  -- the live bookingSlot holding that SO, or null
//   match      -- pickExchangeSo's match ('returnNo' | 'saleOrder'), '' when none
//   mayManage  -- whether the caller may change that booking (owner, staff,
//                 supervisor of the owner's group)
// An SO that is cancelled, closed or already dispatched cannot take the return
// any more, so the RT is a pickup of its own -- EXCEPT an open SO that names
// this RT (match 'returnNo') and is already on a sheet: dispatch put the return
// on the truck with it, so a separate pickup would book the visit twice
// ('WITH_SO'; live 2026-10-09: SO 226100015 names RT 226100011, sheet 46924).
function steerRt ({ soNo: so0, so, soBooking, mayManage, match } = {}) {
  const soNo = docNo(so0)
  if (!soNo || !so) return { action: 'RETURN_ONLY', soNo: soNo }
  const dead = so.canceled === 'Y' || so.status === 'C'
  if (!dead && match === 'returnNo' && text(so.transNo)) return { action: 'WITH_SO', soNo: soNo, transNo: text(so.transNo) }
  if (dead || text(so.transNo)) return { action: 'RETURN_ONLY', soNo: soNo }
  if (!soBooking) return { action: 'BOOK_SO', soNo: soNo }
  return { action: mayManage ? 'ATTACH' : 'REFUSE_OWNED', soNo: soNo }
}

function fail (reason) { return { ok: false, reason: reason } }

// เพิ่ม RT ในการจองนี้. Checked again inside attachRt's transaction.
function checkAttachRt (b, { mayAttach } = {}) {
  if (!b || !R.isActive(b.status)) return fail('ALREADY_CANCELLED')
  if (docNo(b.rtNo)) return fail('HAS_RT')
  if (!docNo(b.soNo)) return fail('NOT_EXCHANGEABLE')
  if (b.proposalStatus === 'pending') return fail('PROPOSAL_PENDING')
  if (!mayAttach) return fail('NOT_ALLOWED')
  return { ok: true }
}

// carBooking's grid mixes `booking` documents and `deliveryList` rows. A
// deliveryList row is status 'SO' (an SO, possibly with a return) or 'RT' (a
// return alone, written by managecar's addRt or by confirming a return-only
// booking).
function isListRow (row) {
  const s = row && row.status
  return s === 'SO' || s === 'RT'
}

// Where the pins of a visit's rows -- bookingSlot, booking or deliveryList --
// go in SAP: an SO row (an exchange included) to its SO, a pickup to its RT. A
// deliveryList return row (status 'RT') is a pickup whatever its soNo says.
// -> { soNos: [..], rtNos: [..] }, each distinct, blanks and 0 dropped.
function pinNumbers (rows) {
  const soNos = []
  const rtNos = []
  const add = (list, n) => { if (n && list.indexOf(n) === -1) list.push(n) }
  ;(rows || []).forEach(r => {
    if (!r) return
    const so = r.status === 'RT' ? '' : docNo(r.soNo)
    if (so) add(soNos, so)
    else add(rtNos, docNo(r.rtNo))
  })
  return { soNos: soNos, rtNos: rtNos }
}

module.exports = {
  RETURN_MINUTES, JOB_TYPES, jobTypeOf, isReturnOnly, hasReturn, isAppointment, refText,
  RT_PROBLEM_TH, rtProblem, rtProblemText, rtDispatched, pickRt, knownRt, rtHeldElsewhere, saleSoOf, pickExchangeSo, returnLines, rtFields, steerRt, checkAttachRt, isListRow, pinNumbers
}
